class SubscriptionWidget extends HTMLElement {
  connectedCallback() {
    this.bindPurchaseOptions();
    this.ensurePlanInputInForm();
    this.bindFormSubmitSync();
    this.removeLegacyRechargePlanInput();
  }

  getSection() {
    return document.getElementById(`shopify-section-${this.dataset.sectionId}`);
  }

  getForm() {
    const id = this.dataset.formId;
    return id ? document.getElementById(id) : null;
  }

  // Our hidden selling_plan input is authored in this block, which renders OUTSIDE
  // the <form> (the form lives in the buy-buttons block). Relying on the form=""
  // association means the theme's `new FormData(form)` can miss it. Move the input
  // to be a real child of the form so it's always part of the submitted payload.
  ensurePlanInputInForm() {
    const form = this.getForm();
    if (!form) return;
    if (!this.sellingPlanInput) {
      this.sellingPlanInput =
        this.querySelector(".subscription-widget__selling-plan-input") ||
        this.getSection()?.querySelector(".subscription-widget__selling-plan-input");
    }
    if (this.sellingPlanInput && this.sellingPlanInput.parentElement !== form) {
      form.appendChild(this.sellingPlanInput);
    }
  }

  // Guarantee the submitted plan matches the visible selection. A capture-phase
  // submit listener runs BEFORE the theme's own form handler builds its FormData,
  // so we write the currently selected frequency (or clear it for one-time) at the
  // last possible moment — no dependence on change events having fired.
  bindFormSubmitSync() {
    const form = this.getForm();
    const section = this.getSection();
    if (!form || !section) return;
    const signal = section._purchaseOptionsAbort?.signal;
    document.addEventListener(
      "submit",
      (event) => {
        if (event.target === form) this.updateSellingPlan(section);
      },
      { capture: true, signal },
    );
  }

  bindPurchaseOptions() {
    const section = this.getSection();
    if (!section) return;

    if (section._purchaseOptionsAbort) {
      section._purchaseOptionsAbort.abort();
    }
    section._purchaseOptionsAbort = new AbortController();
    const { signal } = section._purchaseOptionsAbort;

    this.sellingPlanInput = section.querySelector(
      ".subscription-widget__selling-plan-input",
    );
    section.querySelectorAll("[data-purchase-type]").forEach((radio) => {
      radio.addEventListener(
        "change",
        () => {
          if (radio.dataset.purchaseType === "subscription") {
            this.updatePlanDisplayForCard(
              radio.closest("[data-subscription-card]"),
              section,
            );
          }
          this.updateSellingPlan(section);
        },
        { signal },
      );
    });

    section.querySelectorAll("[data-subscription-card]").forEach((card) => {
      card.addEventListener(
        "click",
        (event) => {
          if (event.target.closest("select")) return;

          const radio = card.querySelector(".subscription-widget__radio");
          if (!radio || radio.disabled || radio.checked) return;

          radio.checked = true;
          radio.dispatchEvent(new Event("change", { bubbles: true }));
        },
        { signal },
      );
    });

    section.querySelectorAll("[data-subscription-plan-select]").forEach((planSelect) => {
      planSelect.addEventListener(
        "change",
        () => {
          const card = planSelect.closest("[data-subscription-card]");
          this.updatePlanDisplayForCard(card, section);
          this.updateSellingPlan(section);
        },
        { signal },
      );
    });

    section
      .querySelectorAll("[data-subscription-group]")
      .forEach((card) => {
        this.updatePlanDisplayForCard(card, section);
      });
    this.updateSellingPlan(section);
  }

  getPurchaseWidget(section = this.getSection()) {
    return section?.querySelector("subscription-widget");
  }

  updatePlanDisplayForCard(card, section = this.getSection()) {
    if (!card || !section) return;

    const planSelect = card.querySelector("[data-subscription-plan-select]");
    if (!planSelect) return;

    const selectedOption = planSelect.selectedOptions[0];
    if (!selectedOption) return;

    const widget = this.getPurchaseWidget(section);
    const showPrice = widget?.dataset.showPrice === "true";
    const salePrice = card.querySelector("[data-subscription-sale-price]");
    const comparePrice = card.querySelector("[data-subscription-compare-price]");

    if (showPrice) {
      if (salePrice && selectedOption.dataset.price) {
        salePrice.textContent = selectedOption.dataset.price;
      }

      if (comparePrice) {
        if (selectedOption.dataset.comparePrice) {
          comparePrice.textContent = selectedOption.dataset.comparePrice;
          comparePrice.hidden = false;
        } else {
          comparePrice.hidden = true;
        }
      }
    }

    // Keep the "Auto-ship & Save X%" title in sync with the chosen frequency.
    const savePercent = card.querySelector("[data-save-percent]");
    if (savePercent && selectedOption.dataset.discount != null && selectedOption.dataset.discount !== "") {
      savePercent.textContent = selectedOption.dataset.discount;
    }
  }

  updateSellingPlan(section = this.getSection()) {
    const selected = section?.querySelector('[name^="purchase_option_"]:checked');
    if (!selected || !this.sellingPlanInput) return;

    if (selected.dataset.purchaseType === "one-time") {
      this.sellingPlanInput.value = "";
      return;
    }

    const planSelect = selected
      .closest("[data-subscription-card]")
      ?.querySelector("[data-subscription-plan-select]");
    if (planSelect) {
      this.sellingPlanInput.value = planSelect.value;
    }
  }

  // Safety net for the legacy ReCharge on-site widget. If the ReCharge widget app
  // embed is still active, it injects its own hidden `selling_plan` input into the
  // product form, which competes with ours at add-to-cart. Strip any selling_plan
  // input that isn't ours (or a sticky-bar mirror from recharge-buy-box-sync.js) so
  // only our selection is submitted.
  removeLegacyRechargePlanInput() {
    const section = this.getSection();
    if (!section) return;
    const strip = () => {
      section.querySelectorAll('input[name="selling_plan"]').forEach((el) => {
        if (el.classList.contains("subscription-widget__selling-plan-input")) return;
        if (el.hasAttribute("data-rc-mirrored")) return;
        el.remove();
      });
    };
    strip();
    if (!section._rcPlanStripObserver) {
      section._rcPlanStripObserver = new MutationObserver(strip);
      section._rcPlanStripObserver.observe(section, { childList: true, subtree: true });
    }
  }
}

customElements.define("subscription-widget", SubscriptionWidget);